import React, { useState, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { MdRefresh } from "react-icons/md";
import { MDBInput } from "mdbreact";

import ConstData from "../../lib/ConstData";
import { enWebPage } from "../../lib/ConstCommand";

import { getIsMobile } from "../../lib/func/ExportFunction";
import { isAndroidWebView } from "../../lib/HandsUpClient";

import Alerts from "./list/Alerts";
// import QRLoginModal from "./modal/QRLoginModal";

const ROTATE_CAPTURE_STORAGE_KEY = "qrLoginRotateCapture";

// 20260819 QR 로그인 화면의 크롬북 전용 화면 공유 보정 옵션 표시 여부를 확인한다.
const isChromeOSBook = () => navigator.userAgent && navigator.userAgent.toLowerCase().indexOf("cros") > -1;

// 20260819 크롬북 화면 공유 방향 보정 설정을 안전하게 불러온다.
const getSavedRotateCaptureSetting = () => {
    try {
        const savedValue = window.localStorage.getItem(ROTATE_CAPTURE_STORAGE_KEY);
        return {
            isRemembered: savedValue !== null,
            isEnabled: savedValue === "-90"
        };
    } catch (err) {
        return { isRemembered: false, isEnabled: false };
    }
}

const getSelectBoxSize = (memberLength) => {
    console.log("getSelectBoxSize - memberLength[", memberLength, "]");
    if (getIsMobile() || memberLength <= 0) {
        return 1;
    } else {
        return 5;
    }
}

const QRLogInWindow = ({ live_info, list_member, isLoginDataResolved, loginEntryFailure, alertKind, loginMessage, isInvalidRoute, currentPageType, handleSelectedFunc }) => {
    const { t } = useTranslation();

    const savedRotateCaptureSetting = useMemo(() => getSavedRotateCaptureSetting(), []);
    const isChromeBook = useMemo(() => isChromeOSBook(), []);

    // const [isModalOpened, setIsModalOpened] = useState(false);

    const [selectedUserIdx, setSelectedUserIdx] = useState(-1);
    const [selectedUserInfo, setSelectedUserInfo] = useState({ userID: "", userSeq: -1, userNickname: "" });
    const [prevSelectedUserInfo, setPrevSelectedUserInfo] = useState({ userID: "", userSeq: -1, userNickname: "" });
    const [liveStudentNickname, setLiveStudentNickname] = useState("");
    const [isRotateCaptureEnabled, setIsRotateCaptureEnabled] = useState(savedRotateCaptureSetting.isEnabled);
    const [isRememberRotateCapture, setIsRememberRotateCapture] = useState(savedRotateCaptureSetting.isRemembered);

    const [errorMsg, setErrorMsg] = useState(loginMessage);
    const [errorKind, setErrorKind] = useState(alertKind);
    const loginAttemptedRef = useRef(false);
    const loginUnavailableNotifiedRef = useRef(false);

    const selectBoxSize = useMemo(() => getSelectBoxSize(list_member ? list_member.length : 0), [list_member]);

    useEffect(() => {
        if (currentPageType === enWebPage.SmartTVQRLogin || currentPageType === enWebPage.SmartTV) {
            if (live_info && list_member && list_member.length > 0) {
                setErrorKind(ConstData.ALERT_KIND.NONE);
                setErrorMsg("");
                const teacher_info = list_member.find((info, idx) => idx === 0);
                if (teacher_info) {
                    setSelectedUserInfo({
                        userID: teacher_info.userID,
                        userSeq: teacher_info.userSeq,
                        userNickname: teacher_info.userNickname,
                    });
                }
                setSelectedUserIdx(0);
            }
        } else {
            if (live_info && list_member && list_member.length > 0) {
                console.log("클래스 정보 수신 성공!");
                if (isAndroidWebView()) {
                    console.log("안드로이드 Native WebView임");
                    setTimeout(() => {
                        console.log("안드로이드 Native WebView 앱에 멤버 목록 수신 성공 전달");
                        console.log("call window.callClassMemberListReady()");
                        window.callClassMemberListReady();
                        console.log("안드로이드 Native WebView에 전달 완료");
                    }, 150); // 짧은 지연 후 안드로이드 Pro앱에 멤버 목록 수신 성공 전달
                }
            }
        }
    }, [currentPageType, live_info, list_member]);

    useEffect(() => {
        const hasLoginMember = list_member && list_member.length > 0;

        if (!isLoginDataResolved) {
            // 20260820 새로운 로그인 정보 요청에서는 이전 로그인 불가 통지 상태를 초기화한다.
            loginUnavailableNotifiedRef.current = false;
            return;
        }

        if (hasLoginMember) {
            loginUnavailableNotifiedRef.current = false;
            return;
        }

        // 20260814 API 응답 완료 후 로그인 가능한 사용자가 없을 때만 Android 앱에 전달한다.
        if (isLoginDataResolved && !loginUnavailableNotifiedRef.current && isAndroidWebView()) {
            window.onStudentLoginFailed("LOGIN_UNAVAILABLE", loginMessage || "로그인 가능한 사용자가 없습니다.");
            loginUnavailableNotifiedRef.current = true;
        }
    }, [isLoginDataResolved, list_member, loginMessage]);

    useEffect(() => {
        setErrorKind(alertKind);
        setErrorMsg(loginMessage);

        // 20260813 실제 로그인 시도 후 수신한 실패만 Android 앱에 전달한다.
        if (loginAttemptedRef.current && loginMessage) {
            if (isAndroidWebView()) {
                window.onStudentLoginFailed("LOGIN_REJECTED", loginMessage);
            }
            loginAttemptedRef.current = false;
        }
    }, [alertKind, loginMessage]);

    useEffect(() => {
        // 20260820 현재 로그인 시도의 클래스 입장 실패만 Android 앱에 한 번 전달한다.
        if (loginAttemptedRef.current && loginEntryFailure) {
            if (isAndroidWebView()) {
                window.onStudentLoginFailed(loginEntryFailure.errorCode, loginEntryFailure.message);
            }
            loginAttemptedRef.current = false;
        }
    }, [loginEntryFailure]);

    const handleKeyPress = (e) => {
        if (e.key === "Enter") {
            onHandleLogin();
        }
    }

    const onClickSelectedUser = (e) => {
        // console.log("onClickOptionSelectedUser - ", e.target.value, typeof e.target.value);
        if (list_member && list_member.length > 0) {
            let selectedUserSeq = e.target.value;
            /* let selectedIndex = e.target.selectedIndex;
            if (currentPageType === enWebPage.LiveQRLogin) {
                const member_info = list_member.find((info, idx) => idx === selectedIndex);
                if (member_info) {
                    setSelectedUserInfo({
                        userID: member_info.userID,
                        userSeq: member_info.userSeq,
                        userNickname: member_info.userNickname
                    });
                    setIsModalOpened(true);
                }
            }
            setSelectedUserIdx(selectedIndex); */
        }
    }

    const onClickCaptureSelectedUser = (e) => {
        // console.log("onClickCaptureSelectedUser - ", e.target.value, e.target.selectedIndex - 1);
        if (errorMsg !== "") {
            setErrorKind(ConstData.ALERT_KIND.NONE);
            setErrorMsg("");
        }

        // if (!ConstData.IS_LOCAL_VERSION || currentPageType !== enWebPage.LiveQRLogin) return;
        if (!ConstData.IS_LOCAL_VERSION) return;
        if (currentPageType !== enWebPage.LiveQRLogin || currentPageType !== enWebPage.LiveQRLoginPbl) return;

        if (list_member && list_member.length > 0) {
            if (/* !isModalOpened &&  */selectedUserIdx > -1 && selectedUserInfo.userSeq > -1) {
                if (prevSelectedUserInfo.userSeq === -1) {
                    setPrevSelectedUserInfo({
                        userID: selectedUserInfo.userID,
                        userSeq: selectedUserInfo.userSeq,
                        userNickname: selectedUserInfo.userNickname
                    });
                } else {
                    if (selectedUserInfo.userSeq === prevSelectedUserInfo.userSeq) {
                        setPrevSelectedUserInfo({
                            userID: "",
                            userSeq: -1,
                            userNickname: ""
                        });
                        // setIsModalOpened(true);
                    }
                }
            }
        }
    }

    const onChangeSelectedUser = (e) => {
        // console.log("onChangeSelectedUser - ", e.target.selectedIndex - 1);
        if (errorMsg !== "") {
            setErrorKind(ConstData.ALERT_KIND.NONE);
            setErrorMsg("");
        }

        if (list_member && list_member.length > 0) {
            let selectedIndex = e.target.selectedIndex - 1;
            const member_info = list_member.find((info, idx) => idx === selectedIndex);
            if (member_info) {
                setSelectedUserInfo({
                    userID: member_info.userID,
                    userSeq: member_info.userSeq,
                    userNickname: member_info.userNickname
                });

                // if (ConstData.IS_LOCAL_VERSION && currentPageType === enWebPage.LiveQRLogin) {
                if (ConstData.IS_LOCAL_VERSION && (currentPageType === enWebPage.LiveQRLogin || currentPageType === enWebPage.LiveQRLoginPbl)) {
                    if (prevSelectedUserInfo.userSeq > -1 && prevSelectedUserInfo.userSeq !== member_info.userSeq) {
                        setPrevSelectedUserInfo({
                            userID: "",
                            userSeq: -1,
                            userNickname: ""
                        });
                    }
                    // setIsModalOpened(true);
                }
            }
            setSelectedUserIdx(selectedIndex);
        }
    }

    const onChangeLiveStudentNickname = (e) => {
        let str = e.target.value;
        str = str.replace(' ', '');
        if (str.length > 15) {
            str = str.substr(0, 15);
            setErrorKind(ConstData.ALERT_KIND.DANGER);
            setErrorMsg(t("유효성_검사_닉네임_글자수오류", { ns: "user" }));
        } else {
            if (errorMsg !== "") {
                setErrorKind(ConstData.ALERT_KIND.NONE);
                setErrorMsg("");
            }
        }
        setLiveStudentNickname(str);
    }

    // 20260819 기억하기가 활성화된 경우 화면 공유 방향 보정 선택값을 즉시 저장한다.
    const onChangeRotateCapture = (e) => {
        const isEnabled = e.target.checked;
        setIsRotateCaptureEnabled(isEnabled);

        if (isRememberRotateCapture) {
            try {
                window.localStorage.setItem(ROTATE_CAPTURE_STORAGE_KEY, isEnabled ? "-90" : "0");
            } catch (err) {
                console.log("onChangeRotateCapture - can not save rotate capture setting.", err);
            }
        }
    }

    // 20260819 크롬북 화면 공유 방향 보정 선택값의 저장 또는 삭제를 처리한다.
    const onChangeRememberRotateCapture = (e) => {
        const isRemembered = e.target.checked;
        setIsRememberRotateCapture(isRemembered);

        try {
            if (isRemembered) {
                window.localStorage.setItem(ROTATE_CAPTURE_STORAGE_KEY, isRotateCaptureEnabled ? "-90" : "0");
            } else {
                window.localStorage.removeItem(ROTATE_CAPTURE_STORAGE_KEY);
            }
        } catch (err) {
            console.log("onChangeRememberRotateCapture - can not update rotate capture setting.", err);
        }
    }

    const onHandleLogin = () => {
        // console.log("onHandleLogin - selectedUserIdx => ", selectedUserIdx);
        // console.log("onHandleLogin - selectedUserInfo => ", selectedUserInfo);
        if (selectedUserIdx === -1) {
            setErrorKind(ConstData.ALERT_KIND.DANGER);
            setErrorMsg(t("사용자_로그인_문구_1", { ns: "user" }));
            return;
        } else {
            setErrorKind(ConstData.ALERT_KIND.NONE);
            setErrorMsg("");
        }

        if (list_member.length > 0) {
            if (liveStudentNickname === "") {
                loginAttemptedRef.current = true;
                handleSelectedFunc({
                    selectedFunc: "LOGIN_BY_QR",
                    loginInfo: {
                        userID: list_member[selectedUserIdx].userID,
                        userSeq: list_member[selectedUserIdx].userSeq,
                        userNickname: list_member[selectedUserIdx].userNickname,
                        rotateCapture: isChromeBook && isRotateCaptureEnabled
                    }
                });
            } else {
                if (liveStudentNickname.length > 15) {
                    setErrorKind(ConstData.ALERT_KIND.DANGER);
                    setErrorMsg(t("유효성_검사_닉네임_글자수오류", { ns: "user" }));
                    return;
                } else {
                    loginAttemptedRef.current = true;
                    handleSelectedFunc({
                        selectedFunc: "LOGIN_BY_QR",
                        loginInfo: {
                            userID: list_member[selectedUserIdx].userID,
                            userSeq: list_member[selectedUserIdx].userSeq,
                            userNickname: liveStudentNickname,
                            rotateCapture: isChromeBook && isRotateCaptureEnabled
                        }
                    });
                }
            }
        }
    }

    const onClickRefresh = () => {
        window.location.reload();
    }

    return (
        <>
            <div className="login-window d-flex flex-column align-items-center w-100 p-5">
                <div className="d-flex justify-content-between align-items-center p-3">
                    <button type="button" className="hiclasstv-button hiclasstv-blue-text p-1" style={{ visibility: 'hidden' }} disabled>
                        <div className="d-flex flex-column">
                            <MdRefresh className="font-xxlg" style={{ visibility: 'hidden' }} aria-hidden="true" />
                            <span className="font-xxsm">
                                {t("새로고침", { ns: "common" })}
                            </span>
                        </div>
                    </button>
                    <span className="font-xxlg hiclasstv-blue-text p-1" style={{ fontWeight: 400 }} title="LogIn:">
                        {t("LogIn", { ns: "user" })}:
                    </span>
                    <button type="button" className="hiclasstv-button hiclasstv-blue-text p-1" title={t("새로고침", { ns: "common" })} onClick={onClickRefresh}>
                        <div className="d-flex flex-column">
                            <MdRefresh className="font-xxlg" aria-hidden="true" />
                            <span className="font-xxsm">
                                {t("새로고침", { ns: "common" })}
                            </span>
                        </div>
                    </button>
                </div>
                <div className="d-flex flex-column align-items-center p-2">
                    {
                        isInvalidRoute ?
                            <div className="d-flex flex-column align-items-center w-100 p-2">
                                <div className="card web-list-card border-none grey lighten-3">
                                    <div className="input-area transparent">
                                        <div className="input-label medium area-30">
                                            <span className="mr-1">{t("클래스", { ns: "live" })}</span>
                                            <span>|</span>
                                        </div>
                                        <div className="input-data medium area-70">
                                            <div className="text-area">
                                                <span className="hiclass-text" title={t("클래스", { ns: "live" }) + " " + t("정보없음", { ns: "common" })}>
                                                    <b>{t("클래스", { ns: "live" })} {t("정보없음", { ns: "common" })}</b>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="input-area transparent">
                                        <div className="input-label medium area-30">
                                            <span className="mr-1">{t("진행자", { ns: "live" })}</span>
                                            <span>|</span>
                                        </div>
                                        <div className="input-data medium area-70">
                                            <div className="text-area">
                                                <span className="hiclass-text" title={t("진행자", { ns: "live" }) + " " + t("정보없음", { ns: "common" })}>
                                                    <b>{t("진행자", { ns: "live" })} {t("정보없음", { ns: "common" })}</b>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <span className="font-md plus p-2">{t("클래스_비활성화_안내_문구_1", { ns: "live" })}</span>
                                <span className="font-md plus pb-2 px-2">{t("클래스_비활성화_안내_문구_2", { ns: "live" })}</span>
                                {/* <div className="d-flex justify-content-center align-items-center w-100">
                                    <Alerts
                                        kind={ConstData.ALERT_KIND.WARNING}
                                        message={t("클래스_비활성화_안내_문구_1", { ns: "live" }) + "\n" + t("클래스_비활성화_안내_문구_2", { ns: "live" })}
                                        style={{ whiteSpace: 'pre-line', width: '100%', padding: '0.75rem' }}
                                    />
                                </div> */}
                            </div> :
                            <div className="d-flex flex-column justify-content-center align-items-center w-100 p-1">
                                <div className="card web-list-card border-none grey lighten-3">
                                    <div className="input-area transparent">
                                        <div className="input-label medium area-35">
                                            <span className="mr-1">{t("클래스", { ns: "live" })}</span>
                                            <span>|</span>
                                        </div>
                                        <div className="input-data medium area-65">
                                            <div className="text-area">
                                                <span className="hiclass-text" title={live_info.liveName}>
                                                    <b>{live_info.liveName}</b>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="input-area transparent">
                                        <div className="input-label medium area-35">
                                            <span className="mr-1">{t("진행자", { ns: "live" })}</span>
                                            <span>|</span>
                                        </div>
                                        <div className="input-data medium area-65">
                                            <div className="text-area">
                                                <span className="hiclass-text" title={live_info.teacher_info ? live_info.teacher_info.userNickname : ""}>
                                                    <b>{live_info.teacher_info ? live_info.teacher_info.userNickname : ""}</b>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {/* <span className="font-md plus p-2">로그인할 사용자를 선택해 주세요.</span> */}
                            </div>
                    }
                    <select size={selectBoxSize} className="custom-select arrow-1 my-2" value={selectedUserInfo.userSeq} onChange={onChangeSelectedUser} onClickCapture={onClickCaptureSelectedUser}>
                        {
                            list_member && list_member.length > 0 ?
                                <>
                                    <option value="-1" disabled>{t("사용자_로그인_문구_1", { ns: "user" })}</option>
                                    {
                                        currentPageType === enWebPage.SmartTVQRLogin || currentPageType === enWebPage.SmartTV ? 
                                        list_member.map((info, index) => (
                                            <option value={info.userSeq} key={'qrlogin-' + info.userSeq + "-" + index} className={"font-weight-bold"}>
                                                {info.userNickname}
                                            </option>
                                        )) :
                                        list_member.map((info, index) => (
                                            <option value={info.userSeq} key={'qrlogin-' + info.userSeq + "-" + index} className={info.userJoinStatus === 'Y' ? "grey-text" : "font-weight-bold"}>
                                                {info.userNickname} {info.userJoinStatus === 'Y' ? t("접속중", { ns: "user" }) : ''}
                                            </option>
                                        ))
                                    }
                                </> :
                                <option value="-1">{t("사용자_목록_없음", { ns: "user" })}</option>
                        }
                    </select>
                    {
                        list_member && list_member.length > 0 && 
                        <div className="d-flex flex-column align-items-center pt-3 pb-2 area-95" id="inputNicknameArea">
                            <MDBInput type="text" id="inputNn" className="input-form-control" containerClass="input-form m-0" label={t("클래스용_닉네임", { ns: "user" })} labelClass="input-form-label" placeholder={t("유효성_검사_닉네임_입력오류", { ns: "user" })} onInput={onChangeLiveStudentNickname} value={liveStudentNickname} onKeyPress={handleKeyPress} />
                        </div>
                    }
                    {
                        isChromeBook && (currentPageType === enWebPage.LiveQRLogin || currentPageType === enWebPage.LiveQRLoginPbl) &&
                        <div className="d-flex flex-column align-items-start area-95 px-2 pb-2">
                            <div className="custom-control custom-checkbox">
                                <input type="checkbox" className="custom-control-input" id="rotateCapture" checked={isRotateCaptureEnabled} onChange={onChangeRotateCapture} />
                                <label className="custom-control-label" htmlFor="rotateCapture">화면 공유 방향 보정</label>
                            </div>
                            <div className="custom-control custom-checkbox mt-2">
                                <input type="checkbox" className="custom-control-input" id="rememberRotateCapture" checked={isRememberRotateCapture} onChange={onChangeRememberRotateCapture} />
                                <label className="custom-control-label" htmlFor="rememberRotateCapture">이 기기에서 설정 기억하기</label>
                            </div>
                        </div>
                    }
                    {
                        errorMsg !== "" &&
                        <div className="d-flex justify-content-center align-items-center w-100 mb-3">
                            <Alerts kind={errorKind} message={errorMsg} style={{ whiteSpace: 'pre-line', width: '100%' }} />
                        </div>
                    }
                    {
                        list_member && list_member.length > 0 && 
                        <>
                            {
                                /* (!ConstData.IS_LOCAL_VERSION || currentPageType === enWebPage.SmartTV) &&
                                <button className="btn hiclasstv-blue white-text w-100 pt-3" disabled={list_member && list_member.length > 0 ? false : true} onClick={() => onHandleLogin()}>
                                    {t("Login", { ns: "user" })}
                                </button> */
                            }
                            <div className="d-flex w-100 justify-content-center align-items-center pt-3">
                                <button className="btn hiclasstv-blue white-text" disabled={selectedUserInfo ? false : true} onClick={() => onHandleLogin()}>
                                    {t("Login", { ns: "user" })}
                                </button>
                            </div>
                        </>
                    }
                </div>
            </div>
            {
                /* isModalOpened &&
                <QRLoginModal
                    isModalOpened={isModalOpened}
                    selectedUser_info={selectedUserInfo}
                    toggleModalOpened={setIsModalOpened}
                    handleSelectedFunc={handleSelectedFunc}
                /> */
            }
        </>
    );
}

export default QRLogInWindow;
